<template>
	<Dialog
		:dialog-visible="visible"
		:persistent-dialog="true"
		:remove-cancel-button="true"
		:disable-agree-button="saving || countryResolutionPending"
		:agree-loading="saving"
		agree-text="Continue to LiveSwitch"
		:options="{ width: '560px' }"
		@agree="submit">
		<template #dialog-header="{ titleId }">
			<div class="d-flex flex-column align-center w-100 pb-2">
				<img src="https://assets.liveswitch.com/shared/liveswitch-logotype.svg" alt="LiveSwitch" height="32" class="mb-4" />
				<h5 :id="titleId" class="text-h5 text-center mb-2">Welcome to LiveSwitch! Let's get you set up.</h5>
				<p class="text-body-2 text-medium-emphasis text-center mb-0">
					Tell us a little about your organization so we can tailor LiveSwitch to your team.
				</p>
			</div>
		</template>
		<template #dialog-body>
			<v-form ref="formRef" v-model="formValid" @submit.prevent="submit">
				<CharacterLimitedTextField
					v-model="form.name"
					field-noun="organization name"
					v-test="'wizard-organization-name'"
					autocomplete="off"
					hide-details="auto"
					:max-length="ORG_NAME_MAX_LENGTH"
					:rules="nameRules">
					<template #label><FieldLabel label="Organization Name (Required)" /></template>
				</CharacterLimitedTextField>

				<IndustryPicker
					v-model="form.industryIds"
					v-test="'wizard-industry'"
					label="Industry (Required)"
					hint="Pick one or more"
					persistent-hint
					hide-details="auto"
					:rules="industryRules"
					class="w-100 min-width-0" />

				<GoogleMapsAutocomplete
					v-model="addressGeo"
					v-test="'wizard-business-address'"
					label="Business Address (Optional)"
					hide-details="auto"
					prepend-inner-icon="mdi-map-marker-outline"
					clearable />

				<TimezonePicker
					v-model="form.timezone"
					v-test="'wizard-timezone'"
					label="Default Timezone (Required)"
					hide-details="auto"
					:rules="timezoneRules" />
				<div class="text-caption text-medium-emphasis mt-n2">
					Used to schedule calls and format reminders when a timezone isn't provided.
				</div>
			</v-form>
		</template>
	</Dialog>
</template>

<script setup lang="ts">
	import { computed, reactive, ref, watch } from "vue";
	import { useToast } from "vue-toastification";
	import moment from "moment-timezone";
	import Dialog from "./Dialog.vue";
	import TimezonePicker from "./formfields/TimezonePicker.vue";
	import GoogleMapsAutocomplete from "./formfields/GoogleMapsAutocomplete.vue";
	import IndustryPicker from "./settings/IndustryPicker.vue";
	import type { Organization } from "../types/organization/Organization";
	import { ORG_NAME_MAX_LENGTH } from "../api/constants";
	import { calculateTimeout, getMessageFromError } from "../helpers/utilities";
	import type { GeoAddress } from "../helpers/location";
	import { businessAddressToGeoAddress, geoAddressToBusinessAddress } from "../helpers/businessAddress";
	import { getCountryFromService } from "../helpers/phone";
	import api from "../api";
	import { validateFormOrWarn } from "../helpers/Validation";
	import FieldLabel from "@common-ui/components/FieldLabel.vue";

	const props = defineProps<{
		visible: boolean;
		organization: Organization | null;
	}>();

	const emit = defineEmits<{
		(e: "completed", updatedOrganization: Organization): void;
	}>();

	const toast = useToast();

	const form = reactive({
		name: "",
		industryIds: [] as string[] | null,
		defaultCountryCode: "",
		timezone: ""
	});
	const addressGeo = ref<GeoAddress | null>(null);
	const formValid = ref(false);
	const formRef = ref();
	const saving = ref(false);
	const countryDetectionPending = ref(false);
	const countryResolutionPending = computed(() => countryDetectionPending.value && !form.defaultCountryCode);
	let resetFormPromise: Promise<void> | null = null;

	// The length limit itself is no longer listed here — CharacterLimitedTextField's own :max-length
	// rule (code points) covers it. No server-unit backstop needed (LSP-16853/§3.3 arithmetic):
	// ORG_NAME_MAX_LENGTH is 200 code points, so the worst case is 400 UTF-16 units, but contact-api's
	// updateOrganization (organizations.ts:363+) runs no length check of its own on organization.name —
	// the only enforcement is the Organization.name column itself (organization.ts:120-122, bare
	// DataTypes.STRING = varchar(255)), which Postgres counts in CODE POINTS (sweep plan §1). 200 code
	// points never exceeds a 255-code-point column, so there is no server threshold to backstop against
	// (unlike CreateProjectDialog's name field, where the CONTROLLER itself checks raw UTF-16 length).
	const nameRules = computed<Array<(v: string | null | undefined) => boolean | string>>(() => [
		(v) => (!!v && v.trim() !== "") || "Organization name is required."
	]);

	const industryRules = computed<Array<(v: string[] | null | undefined) => boolean | string>>(() => [
		(v) => (!!v && v.length > 0) || "Pick at least one industry."
	]);

	const timezoneRules = computed<Array<(v: string | null | undefined) => boolean | string>>(() => [
		(v) => !!v || "A default timezone is required."
	]);

	watch(
		() => props.visible,
		(open) => {
			if (open) {
				resetFormPromise = resetFormFromOrganization();
				return;
			}
			saving.value = false;
		}
	);

	watch(
		() => props.organization,
		() => {
			if (props.visible) {
				resetFormPromise = resetFormFromOrganization();
			}
		}
	);

	async function resetFormFromOrganization(): Promise<void> {
		const org = props.organization;
		form.name = org?.name ?? "";
		form.industryIds = Array.isArray(org?.Industries) ? org!.Industries!.map((industry) => industry.id) : [];
		form.defaultCountryCode = org?.defaultCountryCode ?? "";
		form.timezone = org?.timezone ?? moment.tz.guess();
		addressGeo.value = businessAddressToGeoAddress(org?.businessAddress ?? null);

		if (!form.defaultCountryCode) {
			const detected = await getCountryFromService();
			// Re-check after the async wait so a manual pick made during the
			// ip2c round-trip wins over the detected value.
			if (!form.defaultCountryCode) {
				form.defaultCountryCode = detected;
			}
			return;
		}
	}

	async function submit(): Promise<void> {
		if (!props.organization || saving.value) {
			// LSP-16852/16859: Continue no longer disables on invalid validity (root CLAUDE.md — "Form
			// action buttons — always enabled, no-op when invalid"), so a click while there is no
			// organization loaded yet, or a save is already in flight, reaches here and silently no-ops.
			// Field-validity blocking is handled separately below via a real form.validate() call — see
			// the comment there for why reading the reactive `formValid` v-model flag here used to be a
			// dead end.
			console.warn("WelcomeWizardDialog.submit: blocked — not saving", {
				hasOrganization: !!props.organization,
				saving: saving.value
			});
			return;
		}

		saving.value = true;
		try {
			// LSP-16852/16859 (PR #3271 review — Finding 1, HIGH): awaits the real v-form validate()
			// instead of trusting the reactive `formValid` v-model flag. Vuetify's silent mount-time
			// validation computes each field's isValid but leaves an untouched field `isPristine`, so a
			// never-focused failing field's isValid is `null` — and VForm's aggregate is `null` too.
			// `!formValid.value` reads `!null` as `true`, so the old guard bailed here on a pristine
			// invalid form (e.g. resetFormFromOrganization() setting industryIds to `[]`) — and because
			// isPristine stayed true, no field ever rendered its red/error state either. The result was a
			// dead end: an enabled Continue button, behind a `:persistent-dialog="true"
			// :remove-cancel-button="true"` dialog, that silently did nothing with no way out. Calling
			// validate() marks every field non-pristine, so the failure is both detected AND shown.
			const validationResult = await validateFormOrWarn(
				formRef.value,
				"[WelcomeWizardDialog] submit: form ref not mounted, cannot validate"
			);
			if (!validationResult) {
				return;
			}
			if (!validationResult.valid) {
				const message = "Some fields need your attention before we can continue. Please check the highlighted fields.";
				toast.error(message, { timeout: calculateTimeout(message) });
				console.warn("WelcomeWizardDialog.submit: blocked — form failed validation, not saving", {
					organizationId: props.organization.id,
					errors: validationResult.errors
				});
				return;
			}

			await ensureDefaultCountryCode();
			// Detection kicked off in resetFormFromOrganization() may still be in flight;
			// resolve a default here rather than persisting a null country.
			if (!form.defaultCountryCode) {
				form.defaultCountryCode = await getCountryFromService();
			}
			const updatedOrganization = await api.updateOrganization(props.organization.id, {
				name: form.name.trim(),
				industryIds: form.industryIds ?? [],
				defaultCountryCode: form.defaultCountryCode || null,
				timezone: form.timezone || null,
				businessAddress: geoAddressToBusinessAddress(addressGeo.value)
			});
			emit("completed", updatedOrganization);
		} catch (error) {
			const message = getMessageFromError(error) || "We couldn't save your setup. Please try again.";
			toast.error(message, { timeout: calculateTimeout(message) });
		} finally {
			saving.value = false;
		}
	}

	async function ensureDefaultCountryCode(): Promise<void> {
		if (form.defaultCountryCode) {
			return;
		}

		await resetFormPromise;
		if (form.defaultCountryCode) {
			return;
		}

		countryDetectionPending.value = true;
		try {
			form.defaultCountryCode = await getCountryFromService();
		} finally {
			countryDetectionPending.value = false;
		}
	}
</script>

<style scoped>
	:deep(.v-form) {
		display: flex;
		flex-direction: column;
		gap: 16px;
	}
</style>
